import React, { useState, useEffect, useRef } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useParams, useNavigate } from 'react-router-dom';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { Checkbox } from 'primereact/checkbox';
import { classNames } from 'primereact/utils';
import ArtikalService from '../../service/ArtikalService';

import { useJm, useArtikal } from '../../store/index';

import { useYupValidationResolver } from '../../utilities/useYupValidationResolver';
import * as yup from 'yup';
import i18n from '../../i18n';

const validationSchema = yup.object().shape({
    naziv: yup.string().required('naziv is required.'),
    jm_id: yup.string().required('jm_id is required.'),
    aktivan: yup.string().required('aktivan is required.')
});

function ArtikalForm() {
    const [model, setModel] = useState([]);
    const [deleteModelDialog, setDeleteModelDialog] = useState(false);
    const toast = useRef(null);
    const navigate = useNavigate();
    const { id } = useParams();

    const defaultValues = {
        naziv: '',
        jm_id: '',
        aktivan: '1'
    };

    const { getArtikal, deleteArtikal, createArtikal, updateArtikal } = ArtikalService();
    const { jms } = useJm((state) => ({ jms: state.jms }));
    const { fetchArtikli } = useArtikal();

    const resolver = useYupValidationResolver(validationSchema);

    const {
        control,
        formState: { errors, isDirty },
        handleSubmit,
        reset,
        setFocus
    } = useForm({ defaultValues, resolver });

    useEffect(() => {
        setFocus('naziv');
    }, [setFocus]);

    useEffect(() => {
        if (id) {
            getArtikal(id).then((res) => {
                setModel(res.data);
                reset(res.data);
            });
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const onSubmit = (data) => {
        if (data.id) {
            updateArtikal(data.id, data).then(
                (resolve) => {
                    fetchArtikli();
                    setModel(resolve.data);
                    reset(resolve.data);
                    toast.current.show({ severity: 'success', summary: i18n.t('success'), detail: i18n.t('success_edit'), life: 3000 });
                    navigate('/artikal');
                },
                (reject) => {
                    reject.errors.forEach((error) => {
                        toast.current.show({ severity: 'error', summary: i18n.t('error'), detail: error, life: 3000 });
                    });
                }
            );
        } else {
            createArtikal(data).then(
                (resolve) => {
                    fetchArtikli();
                    setModel(resolve.data);
                    reset(resolve.data);
                    toast.current.show({ severity: 'success', summary: i18n.t('success'), detail: i18n.t('success_create'), life: 3000 });
                    navigate('/artikal');
                },
                (reject) => {
                    reject.errors.forEach((error) => {
                        toast.current.show({ severity: 'error', summary: i18n.t('error'), detail: error, life: 3000 });
                    });
                }
            );
        }
    };

    const getFormErrorMessage = (name) => errors[name] && <small className="p-error">{errors[name].message}</small>;
    const hideDeleteModelDialog = () => setDeleteModelDialog(false);
    const confirmDeleteModel = () => setDeleteModelDialog(true);

    const deleteModel = () => {
        deleteArtikal(model.id).then(() => {
            hideDeleteModelDialog();
            toast.current.show({ severity: 'success', summary: i18n.t('success'), detail: i18n.t('success_deleted'), life: 3000 });
            navigate('/artikal');
        });
    };

    const deleteModelDialogFooter = (
        <>
            <Button label={i18n.t('no')} icon="pi pi-times" outlined onClick={hideDeleteModelDialog} />
            <Button label={i18n.t('yes')} icon="pi pi-check" severity="danger" onClick={deleteModel} />
        </>
    );

    return (
        <div className="form-artikal">
            <Toast ref={toast} />
            <div className="surface-card border-round shadow-2 p-4">
                <div className="grid">
                    <div className="col-12 ">
                        <span className="text-900 text-2xl font-medium mb-4 block">
                            {i18n.t('artikal.model')} {id && `#${id}`}
                        </span>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="grid formgrid p-fluid px-1">
                                <div className="field md:col-6 col-6">
                                    <label htmlFor="naziv" className={classNames({ 'p-error': errors.naziv })}>
                                        {i18n.t('artikal.naziv')}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <Controller name="naziv" control={control} render={({ field, fieldState }) => <InputText id={field.name} {...field} className={classNames({ invalid: fieldState.invalid })} />} />
                                    {getFormErrorMessage('naziv')}
                                </div>
                                <div className="field md:col-1 col-6">
                                    <label htmlFor="jm_id" className={classNames({ 'p-error': errors.jm_id })}>
                                        {i18n.t('artikal.jm_id')}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <Controller
                                        name="jm_id"
                                        control={control}
                                        render={({ field, fieldState }) => <Dropdown id={field.name} {...field} options={jms} optionLabel="naziv" optionValue="id" className={classNames({ invalid: fieldState.invalid })} />}
                                    />
                                    {getFormErrorMessage('jm_id')}
                                </div>
                                <div className="field md:col-2 col-6">
                                    <label htmlFor="aktivan" className={classNames('w-12', { 'p-error': errors.aktivan })}>
                                        {i18n.t('artikal.aktivan')}
                                        <span className="text-red-500">*</span>
                                    </label>
                                    <Controller
                                        name="aktivan"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                            <Checkbox
                                                inputId={field.name}
                                                checked={field.value ? true : false}
                                                {...field}
                                                onChange={(e) => {
                                                    field.onChange(e.checked ? 1 : 0);
                                                }}
                                                className={classNames({ invalid: fieldState.invalid })}
                                            />
                                        )}
                                    />
                                    {getFormErrorMessage('aktivan')}
                                </div>
                            </div>
                            <div className="flex justify-content-between flex-wrap">
                                <Button disabled={!isDirty} outlined icon="pi pi-save" label={id ? i18n.t('edit') : i18n.t('add')} type="submit" severity="success" />
                                {id && <Button outlined icon="pi pi-trash" label={i18n.t('delete')} type="button" severity="danger" onClick={confirmDeleteModel} />}
                            </div>
                        </form>
                    </div>
                </div>
            </div>
            <Dialog visible={deleteModelDialog} style={{ width: '32rem' }} breakpoints={{ '960px': '75vw', '641px': '90vw' }} header="Potvrdi" modal footer={deleteModelDialogFooter} onHide={hideDeleteModelDialog}>
                <div className="confirmation-content">
                    <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                    {model && (
                        <span>
                            {i18n.t('delete_confirmation')} <b>{model.naziv}</b>?
                        </span>
                    )}
                </div>
            </Dialog>
        </div>
    );
}

export default ArtikalForm;
